/**
 * Affiliates Page
 * - Lists pet-care professionals who signed up via the landing site's /affiliate-program
 * - Read-only listing with search and pagination
 */

import React, { useEffect, useState } from "react";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TableComponent from "../../components/common/TableComponent";
import { formatDateTime } from "../../components/details/Shared/helpers";
import { fetchAffiliatesAPI } from "../../services/affiliateAPI";

const PER_PAGE = 20;

export default function Affiliates() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAffiliatesAPI({ page, perPage: PER_PAGE, search })
      .then((response) => {
        if (cancelled) return;
        setAffiliates(response.data?.data?.affiliates ?? []);
        const meta = response.data?.pagination ?? {};
        setPagination({
          totalPages: meta.total_pages || 1,
          totalItems: meta.total_count || 0,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to fetch affiliates:", error);
        toast.error(error.response?.data?.meta?.message || "Failed to load affiliates");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search]);

  const columns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "mobile",
      label: "Mobile",
      render: (val, row) => `${row.country_code || ""} ${val || ""}`.trim() || "N/A",
    },
    { key: "created_at", label: "Signed Up On", render: (val) => formatDateTime(val) },
  ];

  return (
    <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
      {/* ✅ Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go Back"
              className="p-2 md:p-3 text-lg md:text-xl text-gray-800 hover:text-blue-600"
            >
              <MdOutlineArrowBackIos />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              Affiliates
            </h1>
          </div>

          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email or mobile"
            className="w-full sm:w-72 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Subtitle */}
        <p className="text-gray-600 text-sm sm:text-base ml-10 md:ml-14">
          Partners who signed up for the affiliate program
          {pagination.totalItems ? ` · ${pagination.totalItems} total` : ""}
        </p>
      </div>

      {/* ✅ Table */}
      <div className="mt-8 md:mt-10 overflow-x-auto">
        {loading ? (
          <div className="text-center text-gray-500 py-10 text-sm md:text-base">
            Loading...
          </div>
        ) : (
          <TableComponent
            columns={columns}
            data={affiliates}
            currentPage={page}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={PER_PAGE}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
